/**
 * Application shell.
 *
 * The header is one brand plus one settings control, and the panel behind that
 * control holds every low-frequency entry point (language, account, console and
 * the full explanation pages). Keeping the header to a single control is what
 * makes the landing page work on a phone, and it is why the about and guide links
 * no longer sit in the top bar.
 *
 * The shell still owns the session: it hydrates the account, switches the UI
 * language and mirrors it onto the profile, signs out, and reveals the console
 * entry only for an account that holds a capability. It also mounts the
 * session-expiry listener, so a rejected cookie is handled on every route.
 */

import { MapPin, Settings2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { Locale } from '@shared/locales'

import { changeLocale, currentLocale } from '../../lib/i18n'
import { useCurrentUser, useLogout, useMe, useUpdateProfile } from '../../hooks/use-auth'
import { PlainButton } from '../ui/button'
import { LandingSettingsMenu } from './landing-settings-menu'
import { SessionExpiryRedirect } from './session-expiry'

export function AppShell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, user, capabilities } = useCurrentUser()
  const logout = useLogout()
  const updateProfile = useUpdateProfile()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsTriggerRef = useRef<HTMLButtonElement>(null)

  // Confirms the session cookie on first render; the cookie itself is invisible
  // to JavaScript, so this request is the only way to learn who is signed in.
  useMe()

  const locale = currentLocale()
  // The console entry appears for any administrator capability, never for a
  // capability the account cannot use.
  const canOpenAdmin = capabilities.length > 0

  async function switchLocale(next: Locale) {
    await changeLocale(next)
    if (isAuthenticated) {
      updateProfile.mutate({ locale: next })
    }
  }

  /**
   * Closes the panel and hands focus back to the control that opened it, so a
   * keyboard user lands where they left off instead of at the top of the page.
   */
  const closeSettings = useCallback(() => {
    setSettingsOpen(false)
    settingsTriggerRef.current?.focus()
  }, [])

  const settingsTrigger = (
    <PlainButton
      ref={settingsTriggerRef}
      variant="ghost"
      size="icon"
      aria-label={t('home.settingsMenuLabel')}
      aria-haspopup="dialog"
      aria-expanded={settingsOpen}
      onClick={() => setSettingsOpen(true)}
    >
      <Settings2 className="size-4" aria-hidden />
    </PlainButton>
  )

  return (
    <div className="flex min-h-dvh flex-col">
      <SessionExpiryRedirect />
      <header className="border-border bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 py-1">
            <MapPin className="text-primary size-5" aria-hidden />
            <span className="text-sm font-semibold">{t('common.appName')}</span>
          </Link>

          <div className="flex items-center gap-2">{settingsTrigger}</div>
        </div>
      </header>

      <LandingSettingsMenu
        open={settingsOpen}
        onClose={closeSettings}
        locale={locale}
        onSelectLocale={(next) => void switchLocale(next)}
        nickname={user?.nickname ?? user?.email?.split('@')[0] ?? null}
        isAuthenticated={isAuthenticated}
        canOpenAdmin={canOpenAdmin}
        onLogout={() => {
          closeSettings()
          logout.mutate(undefined, { onSettled: () => navigate('/') })
        }}
      />

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-border border-t">
        <div className="text-muted-foreground mx-auto flex w-full max-w-5xl flex-col gap-1 px-4 py-6 text-xs sm:px-6">
          <p>{t('common.appTagline')}</p>
        </div>
      </footer>
    </div>
  )
}
